import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useErrorTracking } from '../../utils/errorTracking';
import { checkSessionAfter401 } from '../../utils/sessionCheck';

// Constants
const HEADERS = {
  APPLICATION: 'office.web',
  CONTENT_TYPES: {
    JSON: 'application/json',
    MULTIPART: 'multipart/form-data'
  }
};

const ERROR_MESSAGES = {
  NETWORK: 'Network error. Please check your connection.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  NO_ACCESS: "You don't have access to this.",
  UNEXPECTED: 'An unexpected error occurred'
};

class ApiError extends Error {
  constructor(type, message, details = {}) {
    super(message);
    this.type = type;
    this.details = details;
  }
}

export const createApiWrapper = ({
  token,
  logout,
  showSnackbar,
  trackError,
  userInfo,
}) => {
  const API_URL_PREFIX = process.env.API_URL;

  const logError = (error, _config) => {
    // Redact sensitive data in production
    const sanitizedError = process.env.NODE_ENV === 'production'
      ? {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status
      }
      : {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        error: error.message,
        details: error.response?.data
      };

    console.error('API Error:', sanitizedError);
  };

  const buildHeaders = (config) => {
    const headers = {};

    const headerBuilders = {
      addToken: () => ({ Authorization: `Bearer ${token}` }),
      addContentType: () => ({ 'Content-Type': HEADERS.CONTENT_TYPES.JSON }),
      addContentTypeMedia: () => ({ 'Content-Type': HEADERS.CONTENT_TYPES.MULTIPART }),
      addXApplicationHeader: () => ({ 'x-application': HEADERS.APPLICATION }),
      acceptLanguage: () => ({ 'Accept-Language': config.acceptLanguage })
    };

    return Object.entries(headerBuilders).reduce((acc, [key, builder]) => {
      if (config[key]) {
        return { ...acc, ...builder() };
      }
      return acc;
    }, headers);
  };

  const handleError = async (error, suppressErrorSnackbar = false, sentCredentials = true, requestPath) => {
    // Track the error with our error tracking system
    trackError(error, {
      userId: userInfo?.id,
      userCountry: userInfo?.country,
      business: {
        feature: error.config?.url?.split('/')[0] || 'unknown',
        process: error.config?.method === 'get' ? 'fetch' : 'update',
        impact: error.response?.status >= 500 ? 'high' : 'medium',
        customerType: userInfo?.type
      }
    });

    logError(error);

    if (!suppressErrorSnackbar) {
      if (error.response) {
        // A 401 on a request that sent our token is confirmed before it ends
        // the session: Hydra also uses it for reads it won't allow a live
        // token (GO-3538). A tokenless call's 401 is about that call alone.
        if (error.response.status === 401 && sentCredentials) {
          const sessionExpired = await checkSessionAfter401({
            token,
            logout,
            error,
            // The path the caller asked for, as useApi reports it, not the
            // prefixed URL axios ended up calling.
            endpoint: requestPath ?? error.config?.url,
            method: error.config?.method,
          });
          showSnackbar(
            sessionExpired ? ERROR_MESSAGES.SESSION_EXPIRED : ERROR_MESSAGES.NO_ACCESS,
            'error'
          );
        } else {
          const errorMessage = error.response.data?.message || ERROR_MESSAGES.UNEXPECTED;
          showSnackbar(errorMessage, 'error');
        }
      } else if (error.request) {
        showSnackbar(ERROR_MESSAGES.NETWORK, 'error');
      } else {
        showSnackbar(ERROR_MESSAGES.UNEXPECTED, 'error');
      }
    }

    throw new ApiError(
      error.response ? 'response' : error.request ? 'network' : 'unknown',
      error.message,
      error.response?.data
    );
  };

  /**
   * Make an HTTP request
   * @param {Object} config - Request configuration
   * @param {string} config.method - HTTP method (GET, POST, etc.)
   * @param {string} config.url - Request URL
   * @param {boolean} [config.addPrefix=true] - Add API_URL_PREFIX to the URL
   * @param {boolean} [config.addToken=true] - Add Authorization header with bearer token
   * @param {boolean} [config.addContentType=true] - Add Content-Type header
   * @param {boolean} [config.addXApplicationHeader=true] - Add x-application header
   * @param {boolean} [config.isExternalApi=false] - Use minimal headers for external APIs to avoid CORS issues
   * @param {boolean} [config.suppressErrorSnackbar=false] - Suppress error snackbar notifications
   * @param {string} [config.successMessage] - Show success snackbar with this message
   */
  const request = async (config) => {
    try {
      const url = config.addPrefix ? `${API_URL_PREFIX}/${config.url}` : config.url;

      // For external APIs, use minimal configuration to avoid CORS issues
      // This removes all custom headers and uses bare minimum axios settings
      const axiosConfig = config.isExternalApi
        ? {
            method: config.method,
            url,
            data: config.data,
            params: config.params,
            // Disable default headers that might cause CORS issues
            transformRequest: [(data) => data],
            headers: {}
          }
        : {
            ...config,
            url,
            headers: buildHeaders(config),
          };

      const response = await axios(axiosConfig);

      if (config.successMessage) {
        showSnackbar(config.successMessage, 'success');
      }

      return response;
    } catch (error) {
      if (!axios.isCancel(error)) {
        // For external APIs, suppress error snackbars by default unless explicitly enabled
        const shouldSuppressError = config.isExternalApi
          ? (config.suppressErrorSnackbar !== false)
          : config.suppressErrorSnackbar;
        const sentCredentials = !!config.addToken && !config.isExternalApi;
        return handleError(error, shouldSuppressError, sentCredentials, config.url);
      }
    }
  };

  // Common request configuration
  const defaultConfig = {
    addPrefix: true,
    addToken: true,
    addContentType: true,
    addXApplicationHeader: true
  };

  const get = (url, config = {}) => request({ method: 'GET', url, ...defaultConfig, addContentType: false, ...config });
  const post = (url, data, config = {}) => request({ method: 'POST', url, data, ...defaultConfig, ...config });
  const put = (url, data, config = {}) => request({ method: 'PUT', url, data, ...defaultConfig, ...config });
  const del = (url, config = {}) => request({ method: 'DELETE', url, ...defaultConfig, addContentType: false, ...config });
  const patch = (url, data, config = {}) => request({ method: 'PATCH', url, data, ...defaultConfig, ...config });
  const head = (url, config = {}) => request({ method: 'HEAD', url, ...defaultConfig, addContentType: false, ...config });

  return {
    get,
    post,
    put,
    delete: del,
    patch,
    head,
  };
};

const useApiWrapper = () => {
  const { userInfo, logout } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { trackError } = useErrorTracking();

  return useMemo(() => createApiWrapper({
    token: userInfo.token,
    logout,
    showSnackbar,
    trackError,
    userInfo
  }), [userInfo, logout, showSnackbar, trackError]);
};

export default useApiWrapper;
